import * as R from "ramda";
import React from "react";
import { FaRegEdit, FaRegSave } from "react-icons/fa";
import styled from "styled-components";
import ClickableElement from "../../../components/utils/ClickableElement";

export const AutoWidthClickableElement = styled(ClickableElement)`
  width: auto;
  text-align: left;
`;

export const EditableTitleLinkContainer = styled.div`
  position: relative;
  align-items: center;
  display: flex;
  &:focus-within {
    width: 100%;
  }
`;

interface IStyledFieldsetProps {
  edit: boolean;
  isMainTitle: boolean;
}

export const StyledFieldset = styled.fieldset<IStyledFieldsetProps>(
  ({ theme, isMainTitle, edit }) => `
  display: flex;
  height: auto;
  margin: 0;
  padding: 0 0.5rem 0.5rem 0.2rem;
  width: 100%;
  font-size: inherit;
  font-family: inherit;
  box-sizing:border-box;
  background-color: ${isMainTitle && edit ? `${theme.colors.lightgrey3}` : "inherit"}
  border-radius: 0.5rem;
  border: none;
  &:focus-within {
    border-bottom: ${edit ? `0.4rem solid ${theme.colors.menuBlueLight}` : ""};
  }
`
);

export const TitleLegend = styled.legend`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBlueLight};
`;

interface IToggleEditButtonProps {
  edit: boolean;
}

export const ToggleEditButton = styled(ClickableElement)<IToggleEditButtonProps>`
  right: ${props => (props.edit ? "0em" : "")};
  font-size: inherit;
  width: auto;
  margin: 0;
  padding: 0;
`;

export const Input = styled.input`
  height: auto;
  margin: 0;
  padding: 0;
  width: 100%;
  font-size: inherit;
  font-family: inherit;
  box-sizing: border-box;
  border: none;
  background-color: inherit;
  &:focus {
    outline: none;
  }
`;

interface IEditableTitleProps {
  addTitle: (title: string) => void;
  isMainTitle: boolean;
  currentTitle: string | null;
  defaultTitle?: string;
  fontSize?: number;
  maxWidth?: number;
  removeTitle?: boolean;
  modifyTitle: (title: string) => void;
}

interface IEditSaveButtonProps {
  edit: boolean;
}

export const SaveEditIcon: React.FC<IEditSaveButtonProps> = ({ edit }) => {
  if (edit) {
    return <FaRegSave />;
  }
  return <FaRegEdit />;
};

const EditableTitle: React.FC<IEditableTitleProps> = props => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [edit, setEdit] = React.useState(false);
  const [title, setTitle] = React.useState(props.currentTitle || "");
  const displayedTitle = title && title.length > 0 ? title : props.defaultTitle;
  const { addTitle, currentTitle, modifyTitle } = props;
  const submit = () => {
    const input = inputRef.current;

    if (R.isNil(input)) {
      return;
    }

    if (R.isNil(currentTitle)) {
      addTitle(input.value);
    } else {
      modifyTitle(input.value);
    }
  };
  const toggleEdit = () => {
    if (edit && inputRef.current?.value !== props.currentTitle) {
      submit();
    }

    setEdit(!edit);

    if (!edit) {
      setTimeout(() => inputRef.current && inputRef.current.select(), 0);
    }
  };
  const editTitle = () => inputRef.current && setTitle(inputRef.current.value);
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      toggleEdit();
    }

    if (e.key === "Escape") {
      setTitle(currentTitle || "");
      toggleEdit();
    }
  };

  React.useEffect(() => {
    const checkIfClickedOutside = (e: Event) => {
      if (edit && inputRef.current && !ref.current?.contains(e.target as HTMLElement)) {
        toggleEdit();
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => document.removeEventListener("mousedown", checkIfClickedOutside);
  }, [edit, inputRef]);

  React.useEffect(() => {
    setTitle(props.currentTitle || "");
  }, [props.currentTitle]);

  return (
    <EditableTitleLinkContainer ref={ref}>
      <StyledFieldset isMainTitle={props.isMainTitle} edit={edit}>
        {edit && props.isMainTitle && (
          <TitleLegend>
            {(!props.defaultTitle ? "" : props.defaultTitle.substring(8)) + (!props.removeTitle ? " Title" : "")}
          </TitleLegend>
        )}
        {edit && (
          <Input
            autoFocus={true}
            onChange={editTitle}
            onKeyDown={handleKeyPress}
            ref={inputRef}
            type="text"
            value={title}
            data-testid="title-textbox"
          />
        )}
        {!edit && <AutoWidthClickableElement onClick={toggleEdit}>{displayedTitle} &nbsp;</AutoWidthClickableElement>}
        <ToggleEditButton onClick={toggleEdit} edit={edit} aria-label="edit">
          <SaveEditIcon edit={edit} />
        </ToggleEditButton>
      </StyledFieldset>
    </EditableTitleLinkContainer>
  );
};

export default EditableTitle;
